import { Controller } from "@hotwired/stimulus"

// Segmented OTP code input: per-digit boxes with auto-advance, paste-fill, and
// keyboard navigation. Adapted from RailsBlocks (https://railsblocks.com/docs/two-factor).
//
// Local addition: a `hidden` target kept in sync with the joined digits via
// syncHidden(), so the form submits a single `code` field and the server reads
// one string — the digit boxes themselves are unnamed and never submit.
export default class extends Controller {
  static targets = ["digit", "form", "submitButton", "hidden"]
  static values = {
    autoSubmit: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: true },
    numericOnly: { type: Boolean, default: true },
  }

  connect() {
    const inputs = this._getInputTargets()

    if (this.autofocusValue && inputs[0]) {
      inputs[0].focus()
    }

    this.boundHandleFocus = this.handleFocus.bind(this)

    inputs.forEach((input) => {
      input.addEventListener("focus", this.boundHandleFocus)
    })
  }

  disconnect() {
    this._getInputTargets().forEach((input) => {
      input.removeEventListener("focus", this.boundHandleFocus)
    })
  }

  handleFocus(event) {
    const input = event.target
    setTimeout(() => {
      input.setSelectionRange(input.value.length, input.value.length)
    }, 0)
  }

  isNumber(value) {
    return /^[0-9]$/.test(value)
  }

  handleInput(event) {
    const currentInput = event.target
    const nextInput = this._getNextInput(currentInput)
    const normalizedValue = this._normalizeSingleValue(currentInput.value)
    currentInput.value = normalizedValue
    this.syncHidden()

    if (this._isValidCharacter(normalizedValue)) {
      if (nextInput) {
        nextInput.focus()
      } else {
        this.handleSubmit()
      }
    }
  }

  handleKeydown(event) {
    const currentInput = event.target

    if (event.key === "Backspace" && currentInput.value === "") {
      const prevInput = this._getPreviousInput(currentInput)
      if (prevInput) {
        prevInput.focus()
      }
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault()
      const prevInput = this._getPreviousInput(currentInput)
      if (prevInput) {
        prevInput.focus()
      }
    }

    if (event.key === "ArrowRight") {
      event.preventDefault()
      const nextInput = this._getNextInput(currentInput)
      if (nextInput) {
        nextInput.focus()
      }
    }
  }

  handlePaste(event) {
    event.preventDefault()
    const paste = this._normalizePasteValue((event.clipboardData || window.clipboardData).getData("text"))
    const inputs = this._getInputTargets()

    if (!paste.length || !inputs.length) {
      return
    }

    const characters = paste.slice(0, inputs.length).split("")
    characters.forEach((character, index) => {
      inputs[index].value = character
    })
    this.syncHidden()

    if (characters.length === inputs.length) {
      this.handleSubmit()
      return
    }

    const nextInput = inputs[characters.length]
    if (nextInput) {
      nextInput.focus()
    }
  }

  handleSubmit(event) {
    if (event) {
      event.preventDefault()
    }
    this.syncHidden()

    if (this.hasSubmitButtonTarget && this._isComplete()) {
      this.submitButtonTarget.focus()
    }

    if (this.autoSubmitValue && this.hasFormTarget) {
      this.formTarget.requestSubmit()
    }
  }

  // Mirror the joined digit values into the hidden `code` field on every change.
  syncHidden() {
    if (this.hasHiddenTarget) {
      this.hiddenTarget.value = this._getInputTargets().map((input) => input.value).join("")
    }
  }

  _getInputTargets() {
    if (this.hasDigitTarget) {
      return this.digitTargets
    }

    return Array.from(this.element.querySelectorAll('[data-two-factor-target^="num"]'))
  }

  _getNextInput(currentInput) {
    const inputs = this._getInputTargets()
    const currentIndex = inputs.indexOf(currentInput)
    if (currentIndex !== -1 && currentIndex < inputs.length - 1) {
      return inputs[currentIndex + 1]
    }
    return null
  }

  _getPreviousInput(currentInput) {
    const inputs = this._getInputTargets()
    const currentIndex = inputs.indexOf(currentInput)
    if (currentIndex > 0) {
      return inputs[currentIndex - 1]
    }
    return null
  }

  _isComplete() {
    const inputs = this._getInputTargets()
    return inputs.length > 0 && inputs.every((input) => this._isValidCharacter(input.value))
  }

  _normalizeSingleValue(value) {
    const withoutWhitespace = value.replace(/\s/g, "")
    if (this.numericOnlyValue) {
      return withoutWhitespace.replace(/\D/g, "").slice(-1)
    }

    return withoutWhitespace.slice(-1)
  }

  _normalizePasteValue(value) {
    const withoutWhitespace = value.replace(/\s/g, "")
    if (this.numericOnlyValue) {
      return withoutWhitespace.replace(/\D/g, "")
    }

    return withoutWhitespace
  }

  _isValidCharacter(value) {
    if (this.numericOnlyValue) {
      return this.isNumber(value)
    }

    return value.length === 1
  }
}
